import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Mic, ArrowLeft, RotateCcw, Send, Type, MicOff, Maximize2, Minimize2, Square,
} from "lucide-react";
import Layout from "../components/Layout";
import PreviewNoPHIBanner, { PREVIEW_MODE } from "../components/PreviewNoPHIBanner";
import { useAria } from "../context/AriaContext";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";
import { isUnifiedRouterEnabled } from "../lib/ariaRouter";
import useIsMobile from "../lib/useIsMobile";
import ResumeLastCaseCard from "../components/ResumeLastCaseCard";
import VocabCorrectionRow from "../components/VocabCorrectionRow";
import { emit, EVENTS } from "../lib/analytics";
import { correctTranscript } from "../lib/vocab";

/**
 * Quick Assist — Preview P0 acceptance rewrite (2026-02).
 *
 * Answer-screen model: this page renders exactly three things — the question
 * ARIA heard, the pipeline status, and the answer (or error). NO local mic
 * when the unified router is on (a single ARIA FAB is the entry point on
 * every authenticated page). Silent failures are eliminated: every arrival
 * with `?q=` or `?err=` produces a visible outcome — an answer OR an error
 * with an explicit "Try Again" button.
 *
 * URL contract from the router:
 *   /quick-assist?q=<question>&auto=1   → auto-fire /api/ask
 *   /quick-assist?err=<sr-error-code>   → show visible error + retry
 *   /quick-assist                       → idle (rare — the FAB is the entry)
 */

const ANSWERING_ENABLED = true;
const INPUT_MAX_CHARS = 500;
const SERVER_TIMEOUT_MS = 60_000;
// Local guard: if the request is still pending after this, surface a
// user-visible error so the pipeline never stalls indefinitely. Deliberately
// longer than the server timeout so the axios error wins on the happy path.
const CLIENT_STALL_MS = 65_000;

// Human-friendly copy for the SR error codes surfaced by webkitSpeechRecognition
// (and our own synthetic codes). Never contains PHI or clinical text.
const SR_ERROR_COPY = {
  "no-speech": "I did not hear anything. Please tap the microphone and try again.",
  "no-transcript": "I did not catch a question. Please tap the microphone and try again.",
  "no-audio": "No microphone audio detected. Tap the microphone to try again.",
  "aborted": "Voice input was cancelled. Please tap the microphone and try again.",
  "audio-capture": "The microphone is not available. Check your device audio permissions and try again.",
  "not-allowed": "Microphone permission is blocked. Enable it in your browser settings and try again.",
  "service-not-allowed": "Voice recognition is unavailable on this device. Please type your question below.",
  "network": "Voice recognition needs network access. Reconnect and try again.",
  "language-not-supported": "Voice recognition does not support the selected language. Please type your question below.",
  "start-failed": "The microphone could not start. Please tap again in a moment.",
  "unknown": "Voice recognition ended unexpectedly. Please tap the microphone and try again.",
};

function friendlySrError(code) {
  if (!code) return SR_ERROR_COPY["unknown"];
  return SR_ERROR_COPY[code] || SR_ERROR_COPY["unknown"];
}

export default function QuickAssist() {
  const aria = useAria();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Mobile-keyboard offset for the QA voice card (Mobile ARIA Phase 1).
  // When the iOS/Android soft keyboard opens, visualViewport.height
  // shrinks; without this the fixed-position voice card sits behind
  // the keyboard and the typed textarea/Done button become unreachable.
  // Mirrors the AriaFab keyboard-offset pattern.
  const [kbOffset, setKbOffset] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbOffset(hidden);
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  // Pipeline phases: idle → recognized → checking → answer | error
  const [phase, setPhase] = useState("idle");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [detail, setDetail] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [typed, setTyped] = useState("");
  const [expanded, setExpanded] = useState(false);

  // ── Quick Assist mic controller (2026-08-09) ─────────────────────
  // Page-owned mic surface for the Quick-Assist-specific correction:
  //   • silence-timeout DISABLED   → normal thinking pauses no longer
  //                                  terminate the session mid-question
  //   • Done                       → stopListen → onTranscript → single
  //                                  atomic submit (no review-mode
  //                                  re-tap step)
  //   • Cancel                     → cancelListen → discards, no submit
  //   • Continue listening         → visible after an unexpected end
  //                                  (hard cap, MAX_RESTARTS, or fatal
  //                                  SR error). Restarts recognition
  //                                  with the accumulated transcript
  //                                  seeded so words already spoken are
  //                                  preserved.
  //   • Global FAB / navigation    → UNCHANGED (that path calls
  //                                  aria.beginListen() with no opts,
  //                                  so it still uses SILENCE_MS = 6000
  //                                  and REVIEW mode).
  //   • onend alone                → never submits an incomplete
  //                                  transcript: dispatch surfaces the
  //                                  text via onUnexpectedEnd, not
  //                                  onTranscript, until the user
  //                                  presses Done.
  const [micMode, setMicMode] = useState("idle");   // "idle" | "listening" | "paused" | "denied"
  const [voiceBuffer, setVoiceBuffer] = useState(""); // accumulated across restarts
  const [micErrorCode, setMicErrorCode] = useState(null);
  const submittedRef = useRef(false);  // single-submit contract

  // Session-scoped conversation (2026-02 founder brief). The shared source
  // of truth lives in AriaContext so the floating ARIA overlay and this
  // page carry ONE conversation_id, ONE history, and ONE pending-clarifier
  // flag. Opening/closing the overlay never resets the thread.
  const turns = aria.conversationTurns;
  const conversationId = aria.conversationId;
  const appendTurn = aria.appendConversationTurn;
  const resetConversation = aria.resetConversation;
  const typedRef = useRef(null);
  const abortRef = useRef(null);
  const stallTimerRef = useRef(null);
  const routerOn = isUnifiedRouterEnabled();

  // Ensure the pipeline visibly transitions to "checking" the moment
  // askBackend is called AND cannot stall indefinitely.
  const askBackend = useCallback(async (q) => {
    const rawQ = (q || "").trim();
    // Build extraContext from prior conversation turns so short follow-up
    // utterances like "RP" inherit the parent turn's context tokens
    // (nobel / abutment / conical / …). Without this, mobile speech
    // recognition emits "R P" / "R.P." / "raise" for a bare "RP" and the
    // vocab context gate blocks the correction — leaving the backend
    // slot extractor to catch it (which it now does), but this belt +
    // braces keeps the on-screen "What ARIA heard" line accurate too.
    const priorText = turns
      .flatMap((t) => [t.q || "", t.a || ""])
      .join(" ")
      .slice(-500); // last ~500 chars is plenty for context tokens
    const vocab = correctTranscript(rawQ, {
      minConfidence: "medium",
      extraContext: priorText,
    });
    const cleaned = (vocab.corrected || rawQ).trim();
    if (!cleaned) {
      setErrorMsg(friendlySrError("no-transcript"));
      setPhase("error");
      return;
    }
    if (!ANSWERING_ENABLED) {
      setErrorMsg("Quick Assist is not enabled in this environment.");
      setPhase("error");
      return;
    }
    // Cancel any in-flight request so rapid re-taps never double-fire.
    try { abortRef.current && abortRef.current.abort(); } catch (_) { /* best-effort */ }
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }

    const controller = new AbortController();
    abortRef.current = controller;
    setQuestion(cleaned);
    setAnswer("");
    setErrorMsg("");
    setPhase("checking");

    // Client-side stall guard — never allow the pipeline to hang silently.
    stallTimerRef.current = setTimeout(() => {
      try { controller.abort(); } catch (_) { /* best-effort */ }
      setErrorMsg("Quick Assist did not respond in time. Please try again.");
      setPhase("error");
      emit(EVENTS.ERROR_TIMEOUT, { feature: "ask_the_bench", duration_ms: CLIENT_STALL_MS });
    }, CLIENT_STALL_MS);

    const startedAt = Date.now();
    emit(EVENTS.ASK_LAUNCHED, { feature: "ask_the_bench" });

    try {
      const r = await api.post(
        "/ask",
        {
          question: cleaned.slice(0, INPUT_MAX_CHARS),
          conversation_id: conversationId,
          history: turns.flatMap((t) => [
            { role: "user", content: t.q },
            { role: "assistant", content: t.a },
          ]),
        },
        { signal: controller.signal, timeout: SERVER_TIMEOUT_MS }
      );
      if (controller.signal.aborted) return;
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
      const text = (r && r.data && r.data.answer) || "";
      const detailText = (r && r.data && typeof r.data.detail === "string") ? r.data.detail : "";
      setAnswer(text);
      setDetail(detailText);
      setDetailOpen(false);
      setPhase(text ? "answer" : "error");
      if (text) {
        // Append this turn to the SHARED conversation so the next question
        // (whether typed here or spoken through the FAB overlay) reaches
        // the backend as a follow-up in the same LlmChat session. The
        // context also inspects the answer for a trailing '?' and flips
        // `pendingClarification`, which the FAB uses to route the NEXT
        // utterance back into this same thread.
        appendTurn(cleaned, text);
        try { aria.speak(text); } catch (_) { /* best-effort */ }
      }
      emit(EVENTS.ASK_ANSWERED, {
        feature: "ask_the_bench",
        duration_ms: Date.now() - startedAt,
        error_code: text ? undefined : "empty_answer",
      });
      if (!text) setErrorMsg("Quick Assist returned an empty response. Please try again.");
    } catch (err) {
      if (controller.signal.aborted) return;
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
      setErrorMsg(errMsg(err, "Quick Assist is temporarily unavailable. Please try again."));
      setPhase("error");
      emit(EVENTS.ERROR_CLIENT, {
        feature: "ask_the_bench",
        duration_ms: Date.now() - startedAt,
        error_code: "ask_backend_failed",
      });
    }
  }, [aria, turns, conversationId, appendTurn]);

  // URL-param dispatch: auto-fire /api/ask on ?q=…&auto=1, or show a visible
  // error on ?err=<code>. Idempotent across renders and re-entry with the
  // same q value.
  const lastAutoQRef = useRef(null);
  const lastErrRef = useRef(null);
  useEffect(() => {
    const q = (searchParams.get("q") || "").trim();
    const auto = searchParams.get("auto") === "1";
    const err = searchParams.get("err");

    // Error branch — always surfaces a visible outcome.
    if (err && lastErrRef.current !== err) {
      lastErrRef.current = err;
      setQuestion("");
      setAnswer("");
      setErrorMsg(friendlySrError(err));
      setPhase("error");
      const clean = new URLSearchParams(searchParams);
      clean.delete("err");
      setSearchParams(clean, { replace: true });
      return;
    }
    // Auto-ask branch. Fires whenever the URL contains a NEW ?q= that
    // hasn't been dispatched yet — critical for follow-up voice questions
    // routed through the ARIA overlay. The `lastAutoQRef` guard prevents
    // the same q from re-firing on re-renders, so a typed follow-up in
    // an ongoing thread is never clobbered by a stale URL. The prior
    // `turns.length === 0` gate broke conversation continuity on mobile
    // by silently dropping every follow-up utterance — defect repaired
    // Aug 2026 during founder's live mobile test.
    if (q && auto && lastAutoQRef.current !== q) {
      lastAutoQRef.current = q;
      setPhase("recognized");
      setQuestion(q);
      setTyped(q);
      // Kick off the API call on the next microtask so React paints the
      // "recognized" state first.
      Promise.resolve().then(() => askBackend(q));
      const clean = new URLSearchParams(searchParams);
      clean.delete("q");
      clean.delete("auto");
      setSearchParams(clean, { replace: true });
    }
  }, [searchParams, setSearchParams, askBackend]);

  // If the router hits an intermediate "recognized" show it briefly before
  // "checking" — this is what the effect above does via microtask deferral.

  const submitTyped = useCallback(() => {
    const q = (typed || "").trim();
    if (!q) return;
    // Prime iOS Safari audio playback INSIDE the user-gesture chain so
    // Rachel can auto-speak the answer without a second tap. Safe no-op
    // on desktop. The mic path primes in AriaFab.onClick.
    try { aria.primeAudio && aria.primeAudio(); } catch (_) { /* best-effort */ }
    askBackend(q);
  }, [typed, askBackend, aria]);

  // ── Quick Assist mic handlers (2026-08-09) ─────────────────────
  const submitVoiceOnce = useCallback((text) => {
    // Guarantees "Done submits the complete transcript exactly once":
    // repeated onend events after a submit cannot re-fire askBackend.
    if (submittedRef.current) return;
    const q = (text || "").trim();
    if (!q) return;
    submittedRef.current = true;
    setMicMode("idle");
    setVoiceBuffer("");
    askBackend(q);
  }, [askBackend]);

  const beginVoice = useCallback((seed) => {
    // Mobile ARIA follow-up correction (R2, 2026-08-15) — Rachel's audio
    // must be released BEFORE the mic is requested so iOS Safari can
    // reassign the audio session to the microphone in the SAME user
    // gesture. Order matters:
    //   1. primeAudio()   ← idempotent unlock; no-op after first tap
    //   2. stopAudio()    ← release any live/pending TTS playback so
    //                       the audio session is NOT playback-owned
    //   3. beginListen()  ← final action, still inside the gesture, no
    //                       await between stopAudio() and this call.
    // Note: we do NOT gate step 2 on `aria.speaking` — the iOS audio
    // session can remain occupied for a short window after `speaking`
    // flips to false (audio element pending end / paused-but-holding).
    try { aria.primeAudio && aria.primeAudio(); } catch (_) { /* best-effort */ }
    try { aria.stopAudio && aria.stopAudio(); } catch (_) { /* best-effort */ }
    setMicErrorCode(null);
    submittedRef.current = false;
    const ok = aria.beginListen({
      disableSilenceTimeout: true,
      seedTranscript: seed || "",
      onTranscript: (text) => submitVoiceOnce(text),
      onUnexpectedEnd: (text, reason, code) => {
        // Never auto-submit on an unexpected end. Preserve the
        // transcript and surface a Continue-listening opportunity, or
        // a permission-denied fallback for fatal errors.
        setVoiceBuffer(text || "");
        if (
          reason === "fatal_error" &&
          (code === "not-allowed" ||
            code === "service-not-allowed" ||
            code === "audio-capture" ||
            code === "no-audio" ||
            code === "network")
        ) {
          setMicErrorCode(code || "unknown");
          setMicMode("denied");
          return;
        }
        // hard_cap_reached / sr_ended / no_speech / silence → "paused"
        // state with Continue-listening + Done + Cancel actions.
        setMicMode("paused");
      },
    });
    if (ok) {
      setMicMode("listening");
    } else if (!aria.recognitionAvailable) {
      setMicErrorCode("service-not-allowed");
      setMicMode("denied");
    } else {
      setMicErrorCode("start-failed");
      setMicMode("denied");
    }
  }, [aria, submitVoiceOnce]);

  const startVoice = useCallback(() => {
    setVoiceBuffer("");
    beginVoice("");
  }, [beginVoice]);

  const doneVoice = useCallback(() => {
    // stopListen sets endReason="stop"; onend → _dispatch → onTranscript.
    // With the tightened dispatch, an empty transcript at Done routes
    // through onUnexpectedEnd("no_speech") instead of submitting empty.
    aria.stopListen();
  }, [aria]);

  const cancelVoice = useCallback(() => {
    aria.cancelListen();
    setVoiceBuffer("");
    setMicMode("idle");
    setMicErrorCode(null);
  }, [aria]);

  const continueVoice = useCallback(() => {
    // Fresh user gesture — restart SR with the accumulated buffer as
    // seed so words already spoken are preserved.
    beginVoice(voiceBuffer);
  }, [beginVoice, voiceBuffer]);

  const submitPausedVoice = useCallback(() => {
    // Paused → user is satisfied with what was captured — submit now.
    submitVoiceOnce(voiceBuffer);
  }, [voiceBuffer, submitVoiceOnce]);

  // Ask a follow-up — clear the input + status surface so the next
  // question can be composed, but PRESERVE the conversation. The
  // shared conversation_id and accumulated conversationTurns in
  // AriaContext must survive this action so the follow-up carries
  // history in its /api/ask payload. (Mobile ARIA follow-up
  // correction R1, 2026-08-15.)
  const askFollowUp = useCallback(() => {
    setQuestion("");
    setAnswer("");
    setDetail("");
    setDetailOpen(false);
    setErrorMsg("");
    setTyped("");
    setPhase("idle");
    // Reset the page-owned mic controller so a new question starts
    // with a clean transcript buffer. Does NOT touch conversation.
    setMicMode("idle");
    setVoiceBuffer("");
    setMicErrorCode(null);
    submittedRef.current = false;
    if (aria.recognitionAvailable) {
      // Fire the corrected controller inside this user-gesture click
      // so WebKit still authorises SR.start() on iOS.
      startVoice();
    }
  }, [aria, startVoice]);

  // Start new conversation — explicit, less-prominent secondary
  // action. Only this button may call resetConversation() and thus
  // rotate conversationId + clear conversationTurns.
  const startNewConversation = useCallback(() => {
    setQuestion("");
    setAnswer("");
    setDetail("");
    setDetailOpen(false);
    setErrorMsg("");
    setTyped("");
    setPhase("idle");
    resetConversation();
    setMicMode("idle");
    setVoiceBuffer("");
    setMicErrorCode(null);
    submittedRef.current = false;
    // Do NOT auto-start voice here — an explicit new-conversation
    // action should give the clinician a clean idle state.
  }, [resetConversation]);

  // ── Cleanup on unmount ──────────────────────────────────────────────
  // NOTE: this MUST NOT depend on `aria` — the aria value memo re-derives
  // when `listening` / `transcript` / etc. change, and any dep array
  // that includes `aria` would fire the cleanup on every render, calling
  // cancelListen() mid-session and killing the active recording.
  // Instead we hold a ref to cancelListen and only call it on true unmount.
  const cancelListenRef = useRef(aria.cancelListen);
  useEffect(() => { cancelListenRef.current = aria.cancelListen; });
  useEffect(() => () => {
    try { abortRef.current && abortRef.current.abort(); } catch (_) { /* best-effort */ }
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    // Quick Assist mic-cutoff correction (2026-08-09): unmounting the
    // page must silently cancel any active recognition session so no
    // partial transcript can auto-submit after navigation.
    try { cancelListenRef.current && cancelListenRef.current(); } catch (_) { /* best-effort */ }
  }, []);

  // ── Status label (spoken by ARIA elsewhere; shown here for the pipeline) ──
  const statusLabel =
    phase === "checking" ? "Checking Quick Assist…" :
    phase === "recognized" ? "Question recognized." :
    phase === "answer" ? "Answer ready." :
    phase === "error" ? "Something went wrong." :
    "Tap the ARIA microphone to ask a clinical question.";

  // ── Mobile answer-mode collapse (2026-08-16, founder mobile P0) ──
  // After a successful submission on a mobile viewport, collapse the
  // large typed composer + "What ARIA heard" echo so the answer area
  // dominates the screen. Every prior Q/A pair is rendered as a
  // compact "YOU ASKED" card (muted slate) above its ARIA response
  // (black + gold). A single sticky bottom control ("Ask a follow-up")
  // reopens the full composer WITHOUT resetting the conversation.
  //
  // NEVER collapse on:
  //   • mic errors / permission-denied / readiness timeouts (micMode
  //     is "denied" — typed fallback MUST remain visible),
  //   • server errors / empty answers (phase === "error"),
  //   • checking / recognized / idle phases.
  const composerCollapsed =
    isMobile &&
    phase === "answer" &&
    micMode !== "denied" &&
    turns.length > 0;

  const answerPanel = (
    <div
      data-testid="quick-assist-answer-panel"
      className={
        expanded
          ? "fixed inset-0 z-[9998] bg-[var(--pa-bg,#0a0a0a)] p-4 sm:p-6 overflow-y-auto"
          : "bg-[var(--pa-surface,#111)] border border-[var(--pa-gold,#d4af37)]/40 rounded-lg p-4 sm:p-6"
      }
      style={expanded ? {} : { minHeight: "60vh", maxHeight: "75vh", overflowY: "auto" }}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--pa-gold,#d4af37)]">
          Answer
        </div>
        {(phase === "answer" || phase === "checking") && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="quick-assist-expand"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-gold,#d4af37)] transition"
            aria-label={expanded ? "Collapse answer" : "Expand answer"}
          >
            {expanded ? <><Minimize2 size={14} /> Collapse</> : <><Maximize2 size={14} /> Expand</>}
          </button>
        )}
      </div>
      {expanded && question && (
        <div className="mb-4 pb-4 border-b border-[var(--pa-border,#333)]">
          <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim,#666)] mb-1">
            You asked
          </div>
          <div className="text-base text-[var(--pa-text,#fff)]" data-testid="quick-assist-question-echo-fs">
            {question}
          </div>
        </div>
      )}
      <div
        className="text-base sm:text-lg leading-relaxed text-[var(--pa-text,#fff)] whitespace-pre-wrap"
        data-testid="quick-assist-answer"
      >
        {answer || (phase === "checking" ? "" : "")}
        {phase === "checking" && (
          <span className="inline-block align-middle ml-1 animate-pulse text-[var(--pa-gold,#d4af37)]">▍</span>
        )}
      </div>
      {phase === "answer" && detail && (
        <div className="mt-4 pt-4 border-t border-[var(--pa-border,#333)]">
          <button
            type="button"
            onClick={() => setDetailOpen((v) => !v)}
            data-testid="quick-assist-detail-toggle"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--pa-gold,#d4af37)] hover:opacity-80 transition"
            aria-expanded={detailOpen}
            aria-controls="quick-assist-detail"
          >
            {detailOpen ? <><Minimize2 size={14} /> Hide detail</> : <><Maximize2 size={14} /> View detailed framework</>}
          </button>
          {detailOpen && (
            <div
              id="quick-assist-detail"
              data-testid="quick-assist-detail"
              className="mt-3 text-sm sm:text-base leading-relaxed text-[var(--pa-text-muted,#c9c9c9)] whitespace-pre-wrap"
            >
              {detail}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="w-full max-w-3xl mx-auto px-1 sm:px-0">
        <button
          onClick={() => navigate(-1)}
          data-testid="quick-assist-back"
          className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition mb-3"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {PREVIEW_MODE && (
          <div className="mb-4">
            <PreviewNoPHIBanner />
          </div>
        )}

        {isMobile && <ResumeLastCaseCard />}

        <h1
          className="font-serif text-2xl sm:text-3xl text-[var(--pa-text)]"
          data-testid="quick-assist-title"
        >
          Quick Assist
        </h1>

        {/* --- What ARIA heard ------------------------------------------
            Hidden on mobile in the collapsed answer-mode so the answer
            area dominates the screen. Every turn's question is still
            visible in the conversation cards below. ------------------ */}
        {!composerCollapsed && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim,#666)] mb-1">
              What ARIA heard
            </div>
            <div
              data-testid="quick-assist-question-echo"
              className="bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md p-3 text-sm sm:text-base text-[var(--pa-text,#fff)] min-h-[3rem]"
            >
              {question || (phase === "checking" ? "…" : (phase === "error" && !question ? "—" : "—"))}
            </div>
            {aria.vocabCorrections && aria.vocabCorrections.length > 0 && aria.originalTranscript && (
              <VocabCorrectionRow
                corrections={aria.vocabCorrections}
                originalTranscript={aria.originalTranscript}
              />
            )}
          </div>
        )}

        {/* --- Pipeline status (hidden in collapsed mobile answer-mode) - */}
        {!composerCollapsed && (
          <div
            data-testid="quick-assist-status"
            role="status"
            aria-live="polite"
            className="mt-2 text-xs text-[var(--pa-text-muted,#aaa)]"
          >
            {statusLabel}
          </div>
        )}

        {/* --- Conversation cards ---------------------------------------
            Full view: only PRIOR turns (current turn lives in the answer
            panel below).
            Collapsed mobile view: ALL turns as compact "YOU ASKED"
            (muted slate) + ARIA (black + gold) cards — no separate
            answer panel.
            ---------------------------------------------------------- */}
        {(composerCollapsed ? turns : turns.slice(0, -1)).length > 0 && (
          <div
            className={composerCollapsed ? "mt-4 space-y-4" : "mt-4 space-y-3"}
            data-testid="quick-assist-history"
          >
            {!composerCollapsed && (
              <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-muted,#888)]">Conversation</div>
            )}
            {(composerCollapsed ? turns : turns.slice(0, -1)).map((t, i) => (
              <div
                key={i}
                data-testid={`quick-assist-turn-${i}`}
                className={
                  composerCollapsed
                    ? "space-y-2"
                    : "border border-[var(--pa-border,#333)] rounded-md p-3 bg-[var(--pa-bg-2,#111)]/60"
                }
              >
                <div
                  data-testid={`quick-assist-turn-${i}-question`}
                  className={
                    composerCollapsed
                      ? "border border-slate-500/30 bg-slate-500/10 rounded-md p-3"
                      : ""
                  }
                >
                  <div
                    className={
                      composerCollapsed
                        ? "text-[10px] uppercase tracking-widest text-slate-300 mb-1"
                        : "text-[10px] uppercase tracking-widest text-[var(--pa-text-muted,#888)] mb-1"
                    }
                  >
                    You asked
                  </div>
                  <div
                    className={
                      composerCollapsed
                        ? "text-sm text-slate-100"
                        : "text-sm text-[var(--pa-text,#eee)] mb-2"
                    }
                  >
                    {t.q}
                  </div>
                </div>
                <div
                  data-testid={`quick-assist-turn-${i}-answer`}
                  className={
                    composerCollapsed
                      ? "bg-[var(--pa-surface,#111)] border border-[var(--pa-gold,#d4af37)]/40 rounded-md p-3"
                      : ""
                  }
                >
                  <div className="text-[10px] uppercase tracking-widest text-[var(--pa-gold,#d4af37)] mb-1">ARIA</div>
                  <div
                    className={
                      composerCollapsed
                        ? "text-sm sm:text-base leading-relaxed text-[var(--pa-text,#fff)] whitespace-pre-wrap"
                        : "text-sm text-[var(--pa-text,#eee)] leading-snug whitespace-pre-wrap"
                    }
                  >
                    {t.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- Answer OR Error ------------------------------------------
            The current turn's answer panel is skipped in collapsed mobile
            answer-mode because the turn was already rendered above. Error
            and idle states still render here so typed fallback stays
            reachable. --------------------------------------------------- */}
        <div className="mt-4">
          {!composerCollapsed && (phase === "answer" || phase === "checking") && answerPanel}

          {phase === "error" && (
            <div
              data-testid="quick-assist-error"
              className="bg-red-500/10 border border-red-500/40 rounded-lg p-4 sm:p-5 text-sm sm:text-base text-red-200"
              style={{ minHeight: "20vh" }}
            >
              <div className="text-[10px] uppercase tracking-widest text-red-300 mb-1">Error</div>
              <div>{errorMsg || "Quick Assist could not complete this request. Please try again."}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={askFollowUp}
                  data-testid="quick-assist-try-again"
                  className="inline-flex items-center gap-1.5 bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] font-semibold px-4 py-2 rounded-md hover:opacity-90 transition"
                >
                  {aria.recognitionAvailable ? <Mic size={14} /> : <RotateCcw size={14} />}
                  Try Again
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhase("idle");
                    setErrorMsg("");
                    setTimeout(() => typedRef.current && typedRef.current.focus(), 40);
                  }}
                  data-testid="quick-assist-type-instead"
                  className="inline-flex items-center gap-1.5 border border-[var(--pa-border,#333)] text-[var(--pa-text,#fff)] px-4 py-2 rounded-md hover:border-[var(--pa-gold,#d4af37)] transition"
                >
                  <Type size={14} /> Type instead
                </button>
              </div>
            </div>
          )}

          {/* Idle state — no answer yet, no error. Direct users to the FAB. */}
          {phase === "idle" && (
            <div
              data-testid="quick-assist-idle"
              className="bg-[var(--pa-surface,#111)] border border-[var(--pa-border,#333)] rounded-lg p-4 sm:p-5 text-sm text-[var(--pa-text-muted,#aaa)]"
              style={{ minHeight: "20vh" }}
            >
              {routerOn ? (
                <>
                  <div className="flex items-center gap-2 mb-2 text-[var(--pa-text,#fff)]">
                    <Mic size={16} className="text-[var(--pa-gold,#d4af37)]" />
                    <span className="text-sm font-semibold">Tap the microphone to ask a question</span>
                  </div>
                  <div>
                    Ask a general clinical question in your own words. Natural pauses will not
                    end the session — press Done when you are finished.
                  </div>
                </>
              ) : (
                <div>Voice recognition is not connected. Please use the typed input below.</div>
              )}
              {!aria.recognitionAvailable && (
                <div
                  data-testid="quick-assist-no-sr"
                  className="flex items-start gap-2 mt-3 text-xs"
                >
                  <MicOff size={14} className="shrink-0 mt-0.5" />
                  <span>Voice recognition is not available in this browser. Use the typed input below.</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- Typed fallback -------------------------------------------
            Hidden in mobile collapsed answer-mode to maximise the
            answer area. A sticky "Ask a follow-up" control below
            reopens this composer with the conversation preserved.
            Errors, permission failures, and readiness timeouts do NOT
            collapse — typed fallback must remain visible.
            ---------------------------------------------------------- */}
        {!composerCollapsed && (
          <div className="mt-4" data-testid="quick-assist-composer">
            <textarea
              ref={typedRef}
              value={typed}
              onChange={(e) => setTyped(e.target.value.slice(0, INPUT_MAX_CHARS))}
              maxLength={INPUT_MAX_CHARS}
              rows={2}
              placeholder="Or type a general clinical question (no patient info)"
              data-testid="quick-assist-typed-input"
              className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md text-[var(--pa-text,#fff)] text-sm p-3 focus:outline-none focus:border-[var(--pa-gold,#d4af37)]"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-[var(--pa-text-dim,#666)]">
                {typed.length}/{INPUT_MAX_CHARS}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={submitTyped}
                  disabled={!typed.trim() || phase === "checking"}
                  data-testid="quick-assist-submit-typed"
                  className="inline-flex items-center gap-1.5 bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] font-semibold px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <Send size={14} /> Ask
                </button>
                {(phase === "answer" || phase === "error") && (
                  <button
                    type="button"
                    onClick={askFollowUp}
                    data-testid="quick-assist-ask-followup"
                    className="inline-flex items-center gap-1.5 border border-[var(--pa-border,#333)] text-[var(--pa-text,#fff)] px-4 py-2 rounded-md hover:border-[var(--pa-gold,#d4af37)] transition"
                  >
                    <RotateCcw size={14} /> Ask a follow-up
                  </button>
                )}
                {(phase === "answer" || phase === "error") && (
                  <button
                    type="button"
                    onClick={startNewConversation}
                    data-testid="quick-assist-new-conversation"
                    aria-label="Start a new conversation — clears the current thread"
                    className="text-xs text-[var(--pa-text-muted,#888)] hover:text-[var(--pa-gold,#d4af37)] underline underline-offset-2 self-center transition"
                  >
                    Start new conversation
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Spacer for the sticky mobile control bar so page content
            (back-to-dashboard, sign-off) is never obscured by the
            follow-up bar or the FAB. */}
        {composerCollapsed && (
          <div
            data-testid="quick-assist-sticky-spacer"
            aria-hidden="true"
            style={{ height: "calc(120px + env(safe-area-inset-bottom, 0px))" }}
          />
        )}

        {/* Sticky mobile control bar — shown only in collapsed answer-
            mode. Uses dynamic viewport-derived kbOffset + safe-area to
            sit above the Safari toolbar and the on-screen keyboard.
            Never rendered on desktop; never rendered during errors so
            typed fallback stays visible in the in-flow composer. */}
        {composerCollapsed && (
          <div
            data-testid="quick-assist-sticky-bar"
            data-safe-area-bottom="env(safe-area-inset-bottom)"
            data-dvh-container="true"
            className="fixed left-0 right-0 z-40 px-3 pt-2 pb-2 bg-gradient-to-t from-[var(--pa-bg,#0a0a0a)] via-[var(--pa-bg,#0a0a0a)]/95 to-transparent"
            style={{
              bottom: `calc(${kbOffset}px + env(safe-area-inset-bottom, 0px))`,
              paddingRight: "calc(72px + env(safe-area-inset-right, 0px))", /* clear the FAB */
            }}
            role="region"
            aria-label="Follow-up controls"
          >
            <div className="flex items-center gap-2 justify-start">
              <button
                type="button"
                onClick={askFollowUp}
                data-testid="quick-assist-sticky-followup"
                className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full border border-[var(--pa-gold,#d4af37)] bg-[var(--pa-bg-2,#0b0b0b)]/90 text-[var(--pa-gold,#d4af37)] text-sm font-semibold hover:bg-[var(--pa-gold,#d4af37)]/10 transition"
              >
                <RotateCcw size={14} /> Ask a follow-up
              </button>
              <button
                type="button"
                onClick={startNewConversation}
                data-testid="quick-assist-sticky-new-conversation"
                aria-label="Start a new conversation — clears the current thread"
                className="text-xs text-[var(--pa-text-muted,#888)] hover:text-[var(--pa-gold,#d4af37)] underline underline-offset-2 transition"
              >
                New
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <Link
            to="/dashboard"
            data-testid="quick-assist-back-to-dashboard"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-gold,#d4af37)] transition"
          >
            <ArrowLeft size={12} /> Back to Dashboard
          </Link>
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Quick Assist floating microphone (2026-08-09 correction).
          On this route, the global gold AriaFab is hidden (see
          AriaFab.jsx:HIDE_ON). This floating FAB is the ONE and ONLY
          microphone visible on /quick-assist. It drives the pause-
          tolerant controller (disableSilenceTimeout, single-submit
          Done, Continue-listening on unexpected end, hard-cap
          preservation, permission-denied fallback). Every OTHER
          route keeps the original AriaFab and its navigation
          contract unchanged.
         ──────────────────────────────────────────────────────── */}
      {aria.recognitionAvailable && (
        <>
          {/* Expanded status/action card — only when we have something to show */}
          {(micMode === "listening" || micMode === "paused" || micMode === "denied") && (
            <div
              data-testid="qa-fab-card"
              className="fixed left-3 right-3 sm:left-auto sm:right-6 z-50 max-w-none sm:max-w-sm bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-gold,#d4af37)]/40 rounded-lg shadow-2xl shadow-black/50 backdrop-blur-sm p-4"
              style={{
                bottom: `calc(${kbOffset + 88}px + env(safe-area-inset-bottom, 0px))`,
              }}
              role="status"
              aria-live="polite"
            >
              {micMode === "listening" && (
                <div data-testid="qa-mic-listening">
                  <div className="flex items-center gap-2 text-red-400">
                    <span
                      className={
                        aria.micStatus === "starting"
                          ? "inline-block h-2 w-2 rounded-full bg-yellow-400 animate-pulse"
                          : "inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse"
                      }
                      data-testid="qa-mic-indicator"
                      data-mic-status={aria.micStatus}
                    />
                    <span
                      className="text-[10px] uppercase tracking-widest font-semibold"
                      data-testid="qa-mic-status-label"
                    >
                      {aria.micStatus === "starting"
                        ? "Starting microphone…"
                        : aria.micStatus === "hearing"
                        ? "Hearing you"
                        : "Listening — speak now"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-[var(--pa-text-muted,#aaa)]">
                    {aria.micStatus === "starting"
                      ? "Waiting for your device microphone to open. If nothing happens, tap Cancel and try again."
                      : "Natural pauses will not end the session. Tap Done when you are finished."}
                  </div>
                  {(voiceBuffer || aria.transcript) && (
                    <div
                      data-testid="qa-mic-buffer"
                      className="mt-3 text-xs text-[var(--pa-text,#eee)] italic bg-black/40 border border-[var(--pa-border,#333)] rounded-md p-2"
                    >
                      &ldquo;{voiceBuffer}{aria.transcript ? `${voiceBuffer ? " " : ""}${aria.transcript}` : ""}&rdquo;
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={cancelVoice}
                      data-testid="qa-mic-cancel"
                      className="text-sm font-medium min-h-[44px] rounded-md border border-[var(--pa-border,#333)] text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-text,#fff)] hover:border-[var(--pa-text-muted,#aaa)] transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={doneVoice}
                      data-testid="qa-mic-done"
                      className="text-sm font-semibold min-h-[44px] rounded-md bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] hover:opacity-90 transition"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
              {micMode === "paused" && (
                <div data-testid="qa-mic-paused">
                  <div className="flex items-center gap-2 text-[var(--pa-text,#fff)]">
                    <MicOff size={14} className="text-[var(--pa-text-muted,#aaa)]" />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Paused — question preserved</span>
                  </div>
                  <div className="mt-1 text-xs text-[var(--pa-text-muted,#aaa)]">
                    Recognition ended before you tapped Done. Continue listening or submit
                    what you have so far.
                  </div>
                  {voiceBuffer && (
                    <div
                      data-testid="qa-mic-buffer-paused"
                      className="mt-3 text-xs text-[var(--pa-text,#eee)] italic bg-black/40 border border-[var(--pa-border,#333)] rounded-md p-2"
                    >
                      &ldquo;{voiceBuffer}&rdquo;
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={cancelVoice}
                      data-testid="qa-mic-paused-cancel"
                      className="text-sm font-medium min-h-[44px] rounded-md border border-[var(--pa-border,#333)] text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-text,#fff)] hover:border-[var(--pa-text-muted,#aaa)] transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={continueVoice}
                      data-testid="qa-mic-continue"
                      className="text-sm font-semibold min-h-[44px] rounded-md border border-[var(--pa-gold,#d4af37)] text-[var(--pa-gold,#d4af37)] hover:bg-[var(--pa-gold,#d4af37)]/10 transition"
                    >
                      <Mic size={14} className="inline mr-1" /> Continue
                    </button>
                    <button
                      type="button"
                      onClick={submitPausedVoice}
                      data-testid="qa-mic-paused-submit"
                      disabled={!voiceBuffer}
                      className="text-sm font-semibold min-h-[44px] rounded-md bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
              {micMode === "denied" && (
                <div data-testid="qa-mic-denied">
                  <div className="flex items-center gap-2 text-red-300">
                    <MicOff size={14} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">
                      {micErrorCode === "not-allowed"
                        ? "Microphone permission denied"
                        : "Voice recognition unavailable"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-[var(--pa-text-muted,#aaa)]">
                    {friendlySrError(micErrorCode || "unknown")}
                  </div>
                  <div className="mt-1 text-xs text-[var(--pa-text-muted,#aaa)]">
                    Your typed question below is preserved.
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={startVoice}
                      data-testid="qa-mic-retry"
                      className="text-sm font-semibold min-h-[44px] rounded-md border border-[var(--pa-gold,#d4af37)] text-[var(--pa-gold,#d4af37)] hover:bg-[var(--pa-gold,#d4af37)]/10 transition px-3"
                    >
                      <Mic size={14} className="inline mr-1" /> Try again
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Floating gold Quick Assist microphone — visually equivalent
              to AriaFab so the founder's muscle memory is preserved. In
              "listening" mode this button acts as Done — a single tap
              stops SR and submits the complete transcript exactly once.
              In "idle"/"paused"/"denied" it opens a fresh session
              (paused → resume with the accumulated buffer). */}
          <button
            type="button"
            onClick={
              micMode === "listening"
                ? doneVoice
                : micMode === "paused"
                ? continueVoice
                : startVoice
            }
            data-testid="qa-fab"
            data-safe-area-bottom="env(safe-area-inset-bottom)"
            data-dvh-container="true"
            aria-label={
              micMode === "listening"
                ? "Done — submit question"
                : micMode === "paused"
                ? "Continue listening"
                : "Ask a question by voice"
            }
            className="fixed right-4 sm:right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl shadow-black/50 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--pa-gold,#d4af37)] focus:ring-offset-2 focus:ring-offset-[var(--pa-bg,#0a0a0a)]"
            style={{
              bottom: `calc(${kbOffset + 16}px + env(safe-area-inset-bottom, 0px))`,
              background:
                micMode === "listening"
                  ? "linear-gradient(135deg, #ef4444, #b91c1c)"
                  : "linear-gradient(135deg, #E6C36A, #D4AF37 60%, #A8842A)",
              boxShadow:
                micMode === "listening"
                  ? "0 0 0 4px rgba(239,68,68,0.30), 0 0 22px 6px rgba(239,68,68,0.5)"
                  : "0 8px 20px rgba(0,0,0,0.45)",
              animation: micMode === "listening" ? "qaFabPulseRed 1s ease-in-out infinite" : "none",
            }}
          >
            {micMode === "listening" ? (
              <Square size={18} color="#fff" strokeWidth={2} />
            ) : (
              <Mic size={20} color="#fff" strokeWidth={2} />
            )}
            <span className="sr-only">
              {micMode === "listening" ? "Done" : "Ask ARIA a question"}
            </span>
            <span
              className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-[var(--pa-gold,#d4af37)] pointer-events-none"
              data-testid="qa-fab-label"
            >
              {micMode === "listening" ? "DONE" : "ARIA"}
            </span>
          </button>

          <style>{`
            @keyframes qaFabPulseRed {
              0%, 100% { box-shadow: 0 0 0 4px rgba(239,68,68,0.30), 0 0 22px 6px rgba(239,68,68,0.5); }
              50% { box-shadow: 0 0 0 10px rgba(239,68,68,0.0), 0 0 32px 12px rgba(239,68,68,0.7); }
            }
          `}</style>
        </>
      )}
    </Layout>
  );
}
